<template>
    <div class="fileGallery h-100">
        <div class="h-100">
            <div v-show="activeIndex != 0" class="backward fileGalleryButton" @click="backward">
                <i class="inticon-angle-left"/>
            </div>
            <carousel-3d ref="fileGallery" class="m-0" :height="height"
                :width="width" @after-slide-change="onAfterSlideChange">
                <slide v-for="(file, fileIndex) in files" :key="file.id" class="d-flex align-items-center text-center"
                    :class="slideClasses(file)" :index="fileIndex">
                    <span v-if="file.thumbnailUrl">
                        <img v-if="activeFiles[fileIndex]" v-show="isFileVisible(fileIndex)" :alt="file.fileName"
                            loading="lazy" :src="file.thumbnailUrl" @load="onFileLoad">
                    </span>
                    <span v-else class="align-self-center m-auto">
                        Vorschau für diesen Dateityp nicht verfügbar.
                    </span>
                </slide>
            </carousel-3d>
            <div v-show="activeIndex != lastFileIndex" class="forward fileGalleryButton" @click="forward">
                <i class="inticon-angle-right"/>
            </div>
            <div class="open fileGalleryButton" title="Datei ansehen" @click="open">
                <i class="inticon-search"/>
            </div>
        </div>
    </div>
</template>

<script>
    import '@scss/components/file-gallery.scss';
    import {Carousel3d, Slide} from 'vue-carousel-3d';

    export default {
        components: {
            Carousel3d,
            Slide,
        },
        props: {
            files: {
                type: Array,
                required: true,
            },
            active: {
                type: Number,
                required: false,
                default: 0,
            },
            width: {
                type: Number,
                required: false,
                default: undefined,
            },
            height: {
                type: Number,
                required: false,
                default: undefined,
            },
        },
        emits: [
            'change',
        ],
        data() {
            return {
                activeIndex: null,
                activeFiles: {},
                visibleFrom: 0,
                visibleTo: 0,
                loadCount: 0,
                flankSize: 2,
            }
        },
        computed: {
            lastFileIndex() {
                return this.files.length ? this.files.length - 1 : 0
            },
            currentFile() {
                return this.files.length && this.files[this.activeIndex]
            },
        },
        watch: {
            active(index) {
                this.setSlide(index)
            },
        },
        mounted() {
            this.setSlide(this.active)
        },
        methods: {
            activateNextFile() {
                if (!this.loadCount) {
                    return
                }
                this.loadCount -= 1
                const index = this.circularIndex(this.visibleTo - this.loadCount)
                if (this.activeFiles[index] || !this.files[index].thumbnailUrl) {
                    this.activateNextFile()
                } else {
                    // need to use Vue.set to ensure reactivity
                    this.$set(this.activeFiles, index, true)
                }
            },
            onFileLoad() {
                this.activateNextFile()
            },
            circularIndex(index) {
                // assumes that index will never be outside the range -n..(n + n)
                if (index < 0) {
                    return index + this.files.length
                }
                if (index >= this.files.length) {
                    return index - this.files.length
                }
                return index
            },
            isFileVisible(fileIndex) {
                if (this.visibleFrom <= this.visibleTo) {
                    return fileIndex >= this.visibleFrom && fileIndex <= this.visibleTo
                }
                else {
                    return fileIndex >= this.visibleFrom || fileIndex <= this.visibleTo
                }
            },
            isFile(file) {
                const fileExtensions = ['.jpg', '.png', '.webp', '.gif']
                return fileExtensions.some(a => file.name.toLowerCase().endsWith(a))
            },
            isPdf(file) {
                const fileExtensions = ['.pdf']
                return fileExtensions.some(a => file.name.toLowerCase().endsWith(a))
            },
            onAfterSlideChange(index) {
                // called when the user changes slides by dragging the cards
                this.setActiveIndex(index)
            },
            setSlide(index) {
                this.setActiveIndex(index)
                this.$refs.fileGallery.goSlide(index)
            },
            setActiveIndex(index) {
                if (this.activeIndex == index) {
                    return
                }
                if (this.files.length) {
                    // limit index to range 0..files.length
                    this.activeIndex = Math.max(0, Math.min(this.files.length - 1, index))
                    this.visibleFrom = this.circularIndex(this.activeIndex - this.flankSize)
                    this.visibleTo = this.circularIndex(this.activeIndex + this.flankSize)
                    this.loadCount = this.flankSize * 2 + 1
                    this.activateNextFile()
                }
                else {
                    this.activeIndex = 0
                    this.visibleFrom = 0
                    this.visibleTo = 0
                }
                this.$emit('change', this.activeIndex);
            },
            forward() {
                this.setSlide(this.activeIndex + 1)
            },
            backward() {
                this.setSlide(this.activeIndex - 1)
            },
            open() {
                window.open(this.currentFile.url, '_blank')
            },
            slideClasses(file) {
                return {
                    noThumbnail: !file.thumbnailUrl,
                }
            },
        },
    }
</script>
